import {InfiniteData, useQueryClient} from '@tanstack/react-query';
import {useCallback} from 'react';
import {
  BestCommentListQuery,
  CommentItem,
  CommentItemQuery,
  CommentListQuery,
  Maybe,
  MyCommentListQuery,
  ReplyCommentListQuery,
} from '../../../graphql/generated/types';
import {
  useCommentItemQuery,
  useInfiniteBestCommentListQuery,
  useInfiniteCommentListQuery,
  useInfiniteMyCommentListQuery,
  useInfiniteReplyCommentListQuery,
} from '../../../wrapper';

interface Props {
  seriesId?: number;
  productId?: number;
  commentUid?: number;
  parentUid?: number;
  sortType?: Maybe<string>;
}

export function useSetCommentQueryData({
  seriesId,
  productId,
  commentUid,
  parentUid,
  sortType,
}: Props) {
  const queryClient = useQueryClient();

  const setCommentQueryData = useCallback(
    async ({newCommentData}: {newCommentData: CommentItem}) => {
      // commentUid가 넘어온 경우 단일 코멘트.
      if (commentUid) {
        queryClient.setQueryData<CommentItemQuery>(
          useCommentItemQuery.getKey({
            parentUid: commentUid,
            seriesId,
          }),
          oldData => {
            return {
              commentItem: {
                ...oldData?.commentItem,
                ...newCommentData,
              },
            };
          }
        );
      }
      // reply Comment 처리.
      if (parentUid) {
        queryClient.setQueryData<
          InfiniteData<ReplyCommentListQuery | undefined>
        >(
          useInfiniteReplyCommentListQuery.getKey({
            commentListInput: {
              page: 0,
              seriesId,
              productId,
              parentUid,
            },
          }),
          oldData => {
            const newPages = oldData?.pages.map(page => {
              const newCommentList = page?.replyCommentList?.commentList.map(
                comment => {
                  if (comment.commentUid === newCommentData.commentUid) {
                    return {
                      ...comment,
                      ...newCommentData,
                    };
                  }
                  return comment;
                }
              );
              return {
                replyCommentList: {
                  ...page?.replyCommentList,
                  commentList: newCommentList,
                },
              };
            });
            return newPages
              ? ({
                  ...oldData,
                  pages: newPages,
                } as InfiniteData<ReplyCommentListQuery>)
              : oldData;
          }
        );
      }
      // Best Comment 처리
      if (seriesId) {
        queryClient.setQueryData<
          InfiniteData<BestCommentListQuery> | undefined
        >(
          useInfiniteBestCommentListQuery.getKey({
            bestCommentListInput: {
              page: 0,
              seriesId,
              productId,
            },
          }),
          oldData => {
            const newPages = oldData?.pages.map(page => {
              const newCommentList = page.bestCommentList?.commentList.map(
                comment => {
                  if (comment.commentUid === newCommentData.commentUid) {
                    return {
                      ...comment,
                      ...newCommentData,
                    };
                  }
                  return comment;
                }
              );
              return {
                bestCommentList: {
                  ...page.bestCommentList,
                  commentList: newCommentList,
                },
              };
            });

            return newPages
              ? ({
                  ...oldData,
                  pages: newPages,
                } as InfiniteData<BestCommentListQuery>)
              : oldData;
          }
        );
      }

      // All Comment 처리
      queryClient.setQueryData<InfiniteData<CommentListQuery> | undefined>(
        useInfiniteCommentListQuery.getKey({
          commentListInput: {
            page: 0,
            seriesId,
            productId,
            sortType,
          },
        }),
        oldData => {
          const newPages = oldData?.pages.map(page => {
            const newCommentList = page.commentList?.commentList.map(
              comment => {
                if (comment.commentUid === newCommentData.commentUid) {
                  return {
                    ...comment,
                    ...newCommentData,
                  };
                }
                return comment;
              }
            );
            return {
              commentList: {
                ...page.commentList,
                commentList: newCommentList,
              },
            };
          });

          return newPages
            ? ({
                ...oldData,
                pages: newPages,
              } as InfiniteData<CommentListQuery>)
            : oldData;
        }
      );

      //myComment 처리
      queryClient.setQueryData<InfiniteData<MyCommentListQuery | undefined>>(
        useInfiniteMyCommentListQuery.getKey({page: 0}),
        oldData => {
          const newPages = oldData?.pages.map(page => {
            const newCommentList = page?.myCommentList?.commentList.map(
              comment => {
                if (comment.commentUid === newCommentData.commentUid) {
                  return {
                    ...comment,
                    ...newCommentData,
                  };
                }
                return comment;
              }
            );
            return {
              myCommentList: {
                ...page?.myCommentList,
                commentList: newCommentList,
              },
            };
          });
          return newPages
            ? ({
                ...oldData,
                pages: newPages,
              } as InfiniteData<MyCommentListQuery>)
            : oldData;
        }
      );
    },
    [commentUid, seriesId, queryClient, productId, sortType, parentUid]
  );

  return {
    setCommentQueryData,
  };
}
